<template>
    <div class="betSlip comboParlay notranslate">
        <div class="betSlip-area">
            <div class="betSlip-title">
                <span v-on:click="showMiniBetSlip">
                    <SvgIcon iconName="systemIcon_back" />
                </span>

                <div class="betSlip-title-name">
                    {{ betBuilderTitle }}
                </div>
                <div class="betSlip-title-mixParlay-removeAll BetBuilderSlip_Btn_RemoveAll" v-on:click="removeBetSlip">
                    <SvgIcon iconName="systemIcon_removeAll" />
                </div>
            </div>
            <div
                class="betSlip-scrollContent"
                v-bind:class="{ _stakePadShow: !isShowStakePad }"
            >
                <div class="betSlip-ticketArea">
                    <BetBuilderTicket />
                    <TransitionGroup name="fade">
                        <div
                            v-for="n in countOfAddMore"
                            v-bind:key="n"
                            class="betSlip-addMoreBetBuilder"
                            v-on:click="hideBetSlip"
                        >
                            <I18nT v-if="n === 1" keypath="betSlip_betBuilder_addMoreLeg">
                                <template #highlightedText>
                                    <span place="highlightedText" class="highlight">{{ betBuilderAddMoreMsg }}</span>
                                </template>
                            </I18nT>
                            <span v-else>+</span>
                        </div>
                    </TransitionGroup>
                    <div
                        v-if="isValidSubBetCount && ticketCount < 11"
                        class="betSlip-addMoreBetBuilder"
                        v-on:click="hideBetSlip"
                    >
                        <span>+</span>
                    </div>

                    <div v-if="isValidSubBetCount" class="betSlip-info safeAreaBottom">
                        <div class="betSlip-info-totalOdds">
                            <span>{{ i18n.t('betSlip_totalOdds') }}:</span>
                            <span>{{ totalOddsDisplay }}</span>
                        </div>
                        <div class="betSlip-info-totalOdds">
                            <span>{{ i18n.t('betSlip_totalStake') }}:</span>
                            <span>
                                {{ totalStakeDisplay }}
                            </span>
                        </div>
                        <div class="betSlip-info-maxPayout">
                            <span>{{ i18n.t('maxPayout') }}:</span>
                            <span class="notranslate betSlip-info-maxPayout-amount">
                                {{ totalPayoutDisplay }}
                            </span>
                        </div>
                        <PlaceBet
                            class="combo-placeBet"
                            v-bind:stake="stake"
                            v-bind:isDisableBtn="isDisableBetBuilderPlaceBet"
                            v-bind:balance="balance"
                            v-bind:minBet="minBet"
                            v-bind:price="price"
                            v-bind:placeBetGa="'BetBuilderSlip_Btn_PlaceBet'"
                            v-on:placeBet="placeBet"
                            v-on:changeStake="setStake"
                        />
                        <StakePad
                            v-bind:stake="stake"
                            v-bind:balance="balance"
                            v-bind:minBet="minBet"
                            v-bind:maxBet="maxBet"
                            v-bind:currency="customerInfo.currency"
                            v-on:changeStake="setStake"
                        />
                        <SwitchAcceptAnyOddsValue
                            v-if="!customerInfo.isAcceptAnyOdds"
                            class="combo-acceptAnyOdds"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import BetBuilderTicket from '@/components/betSlip/BetBuilderTicket.vue';
    import PlaceBet from '@/components/betSlip/PlaceBet.vue';
    import StakePad from '@/components/betSlip/StakePad.vue';
    import { MarketTab, RoutePage, SportType } from '@/interface/enum';
    import { i18n } from '@/libraries/i18n';
    import { enableScrolling } from '@/libraries/utils/dom';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'BigBetBuilderBetSlip',
        components: {
            PlaceBet,
            StakePad,
            SwitchAcceptAnyOddsValue,
            SvgIcon,
            BetBuilderTicket,
        },
        setup() {
            const { customerInfo, balance } = storeToRefs(useAccountStore());

            const { isShowDepositInTicketEnabled } = storeToRefs(useFeatureToggleStore());

            const { showMiniBetSlip } = useBetSlipStore();

            const {
                betSlip,
                isPlacingBet,
                stake,
                ticketCount,
                totalStakeDisplay,
                totalOddsDisplay,
                totalPayoutDisplay,
                isUpdatingTicket,
                minBet,
                maxBet,
                isValidSubBetCount,
                countOfAddMore,
                price,
                hasBettablePrice,
            } = storeToRefs(useBetBuilderBetSlipStore());

            const {
                placeBet,
                setStake,
                removeBetSlip,
                refresh,
            } = useBetBuilderBetSlipStore();

            const { isShowStakePad } = storeToRefs(useBetSlipStore());

            const { setIsShowStakePad } = useBetSlipStore();

            const betBuilderTitle = computed(() => i18n.t('bet_builder_big_betSlip_title'));

            const betBuilderAddMoreMsg = computed(() => i18n.t('betSlip_betBuilder_addMoreLegHighlightedText', { count: countOfAddMore.value }));

            const isDisableBetBuilderPlaceBet = computed(() => {
                if (isPlacingBet.value) return true;
                if (isUpdatingTicket.value) return true;
                if (!isValidSubBetCount.value) return true;
                if (!hasBettablePrice.value) return true;

                if (balance.value === null) return false;
                if (balance.value < minBet.value) return true;

                return Math.floor(balance.value) <= 0;
            });

            function scrollToBottom() {
                const scrollDiv = document.getElementsByClassName('betSlip-scrollContent')[0];
                scrollDiv?.scrollTo(0, scrollDiv.scrollHeight);
            }

            const ticks = ref(0);
            async function tick() {
                ticks.value++;
                const refreshOpenTicketSeconds = 15;
                if (ticks.value >= refreshOpenTicketSeconds) {
                    await refresh();
                    ticks.value = 0;
                }
            }

            const route = useRoute();
            const router = useRouter();

            function hideBetSlip() {
                showMiniBetSlip();
                const matchIdParam = Array.isArray(route.params.matchId)
                    ? route.params.matchId[0]
                    : route.params.matchId;

                if (
                    route.name !== RoutePage.MatchDetail
                    || parseInt(matchIdParam) !== betSlip.value.match.matchId
                ) {
                    router.push({
                        name: RoutePage.MatchDetail,
                        params: {
                            matchId: betSlip.value.match.matchId.toString(),
                            sportType: SportType.toRouteName(betSlip.value.match.sportType),
                            leagueName: betSlip.value.match.leagueName.replace(/ /g, '-') ?? '',
                            matchName: `${betSlip.value.match.homeTeamName.replace(/ /g, '-')}-vs-${betSlip.value.match.awayTeamName.replace(/ /g, '-')}`,
                        },
                        query: {
                            tab: MarketTab.BetBuilder,
                        },
                    });
                }
            }

            onMounted(() => {
                scrollToBottom();
            });

            setIsShowStakePad(true);

            window.eventEmitter.on('tick', tick);

            onBeforeUnmount(() => {
                window.eventEmitter.off('tick', tick);
                enableScrolling();
            });

            return {
                i18n,
                customerInfo,
                balance,
                betBuilderTitle,
                ticketCount,
                betBuilderAddMoreMsg,
                countOfAddMore,
                isShowStakePad,
                isShowDepositInTicketEnabled,
                removeBetSlip,
                hideBetSlip,
                showMiniBetSlip,
                setStake,
                totalOddsDisplay,
                totalPayoutDisplay,
                stake,
                isValidSubBetCount,
                placeBet,
                minBet,
                maxBet,
                isDisableBetBuilderPlaceBet,
                totalStakeDisplay,
                price,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/betSlipComboParlay";
</style>
